import { Center, IconButton, InputGroup, Spinner, Text } from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { ArrowRightFromLine } from 'lucide-react';
import { Trans, Plural } from '@lingui/react/macro';
import { useMemo, useCallback } from 'react';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { StudentList } from './StudentList';

import { emptyPaginationResponse, parseSortOrder, SortOrder } from '@duke-hq/lib/pagination';
import pickBy from 'lodash-es/pickBy.js';
import { withPermission } from '../withPermission';

import type { StudentsURLParams } from './types';

export const ProviderStudents = withPermission('canViewProviders', () => {
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const { providerId } = useParams<{ providerId: string }>();
  const { pageSize, currentPage, setPage } = usePagination();
  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: ['last_authenticated_at'],
    sort: [SortOrder.DESC],
  });

  const querySearch = searchParams.get('search') ?? '';

  const getURLFilters: StudentsURLParams = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { sort, search, orderBy, ...rest } = urlFilters;
    return {
      ...rest,
      orderBy: orderBy ?? 'last_authenticated_at',
      sort: parseSortOrder(sort) ?? SortOrder.DESC,
    };
  }, [searchParams]);

  const { data: participants, isFetching } =
    trpcAdmin.providers.students.listStudentsWithPagination.useQuery(
      {
        providerId: Number(providerId),
        search: querySearch.trim(),
        filters: {
          orderBy: getURLFilters.orderBy,
          sort: getURLFilters.sort,
        },
        pagination: {
          page: Number(currentPage),
          limit: pageSize,
        },
      },
      {
        initialData: emptyPaginationResponse(),
        placeholderData: (previousData) => previousData,
      },
    );

  const handleFilterChange = useCallback(
    (newFilters: Partial<StudentsURLParams>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters, page: undefined },
        (value) => value != null && value != '',
      );

      const newSearchParams = new URLSearchParams(updatedFilters as Record<string, string>);
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  return (
    <TableViewLayout
      pageHeader={[
        !isExpanded && (
          <IconButton
            aria-label="expand secondary sidebar"
            icon={<ArrowRightFromLine />}
            variant="tertiary"
            onClick={toggleExpandSubSidebar}
            size="md"
          />
        ),
        <PageHeading key="participants">
          <Trans>Participants</Trans>
        </PageHeading>,
      ]}
      filters={
        <InputGroup maxW="xs" size="sm">
          <SearchInput />
        </InputGroup>
      }
      tableActionsLeft={
        <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
          <Plural
            value={participants.pagination.totalCount || 0}
            zero="0 participants"
            one="1 participant"
            other="# participants"
          />
        </Text>
      }
      table={
        isFetching ? (
          <Center p={8}>
            <Spinner size="xl" transitionDelay={'2900ms'} />
          </Center>
        ) : participants.items.length === 0 ? (
          <Center p={8}>
            <Text textAlign="center">
              <Trans>No participants found</Trans>
            </Text>
          </Center>
        ) : (
          <StudentList
            students={participants.items}
            urlParams={getURLFilters}
            onFilters={handleFilterChange}
          />
        )
      }
      pagination={
        participants.items.length > 0 ? (
          <Pagination pagination={participants.pagination} onPageChange={setPage} />
        ) : null
      }
    />
  );
});
